import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { QueueAction } from '@rossum/api-client/schemas';
import {
  IconAlertOctagon,
  IconAlertTriangle,
  IconInfoSquareRounded,
} from '@rossum/ui/icons/tabler';
import { Autocomplete, TextField } from '@rossum/ui/material';
import { getUnsafe } from '../../../lib/helpers';
import { getSchemaFields } from '../../formulas/FormulaEditor/helpers';
import { createForm } from './createForm';
import { createHeader } from './createHeader';

const messageActionPayloadSchema = (intl?: IntlShape) =>
  z.object({
    type: z.string().default('warning'),
    content: z
      .string()
      .default('')
      .refine(
        val => val.length > 0,
        intl?.formatMessage({
          id: 'features.queueSettings.rules.actions.message.payload.content.required',
        })
      ),
    schema_id: z.string().optional(),
  });

const MessageActionForm = createForm(
  ({ value, onChange, schemaContent, errors, alreadyUsedSchemaIds }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () =>
        getSchemaFields(schemaContent).filter(
          field => field.category === 'datapoint'
        ),
      [schemaContent]
    );
    const schemaField = useMemo(
      () => schemaFieldOptions.find(f => f.id === value.schema_id),
      [schemaFieldOptions, value.schema_id]
    );

    return (
      <>
        <Autocomplete
          size="small"
          options={schemaFieldOptions}
          getOptionLabel={option => `${option.label} (${option.id})`}
          getOptionDisabled={option =>
            alreadyUsedSchemaIds?.includes(option.id) ?? false
          }
          value={schemaField}
          onChange={(_e, v) => {
            if (v) {
              onChange({ ...value, schema_id: v.id });
            } else {
              onChange({ ...value, schema_id: undefined });
            }
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={intl.formatMessage({
                id: 'features.queueSettings.rules.actions.message.form.placement',
              })}
              placeholder={intl.formatMessage({
                id: 'features.queueSettings.rules.actions.message.form.placementPlaceholder',
              })}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        <TextField
          multiline
          maxRows={10}
          label={intl.formatMessage({
            id: 'features.queueSettings.rules.actions.message.form.content',
          })}
          size="small"
          value={value.content}
          onChange={e => onChange({ ...value, content: e.currentTarget.value })}
          error={getUnsafe(errors, 'content') !== undefined}
          helperText={getUnsafe(errors, 'content.message')?.toString()}
        />
      </>
    );
  },
  (intl?: IntlShape) => messageActionPayloadSchema(intl)
);

const MessageActionHeader = createHeader(
  ({ value, schemaContent, RenderComponent }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () => getSchemaFields(schemaContent),
      [schemaContent]
    );
    const schemaField = useMemo(
      () => schemaFieldOptions.find(f => f.id === value.schema_id),
      [schemaFieldOptions, value.schema_id]
    );
    return (
      <RenderComponent
        entries={[
          {
            key: 'placement',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.actions.message.form.placement',
            }),
            value:
              schemaField?.label ??
              value.schema_id ??
              intl.formatMessage({
                id: 'features.queueSettings.rules.actions.message.form.placementHeader',
              }),
            sx: {
              maxWidth: 200,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
          },
          {
            key: 'content',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.actions.message.form.content',
            }),
            value: value.content,
            sx: {
              maxWidth: 400,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
          },
        ]}
      />
    );
  },
  (intl?: IntlShape) => messageActionPayloadSchema(intl)
);

const infoAction = {
  icon: IconInfoSquareRounded,
  intlKey: 'message.info' as const,
  groupIntlKey: 'messages' as const,
  resolveAction: (a: QueueAction) => {
    return (
      a.type === 'show_message' &&
      (a.payload as { type: string }).type === 'info'
    );
  },
  serialized: { type: 'show_message' as const, payload: { type: 'info' } },
  form: MessageActionForm,
  header: MessageActionHeader,
  supportedEvents: ['validation'] as const,
  payloadSchema: (intl?: IntlShape) => messageActionPayloadSchema(intl),
};

const warningAction = {
  icon: IconAlertTriangle,
  intlKey: 'message.warning' as const,
  groupIntlKey: 'messages' as const,
  resolveAction: (a: QueueAction) => {
    return (
      a.type === 'show_message' &&
      (a.payload as { type: string }).type === 'warning'
    );
  },
  serialized: { type: 'show_message' as const, payload: { type: 'warning' } },
  form: MessageActionForm,
  header: MessageActionHeader,
  supportedEvents: ['validation'] as const,
  payloadSchema: (intl?: IntlShape) => messageActionPayloadSchema(intl),
};

const errorAction = {
  icon: IconAlertOctagon,
  intlKey: 'message.error' as const,
  groupIntlKey: 'messages' as const,
  resolveAction: (a: QueueAction) => {
    return (
      a.type === 'show_message' &&
      (a.payload as { type: string }).type === 'error'
    );
  },
  serialized: { type: 'show_message' as const, payload: { type: 'error' } },
  form: MessageActionForm,
  header: MessageActionHeader,
  supportedEvents: ['validation'] as const,
  payloadSchema: (intl?: IntlShape) => messageActionPayloadSchema(intl),
};

export const messageActions = [infoAction, warningAction, errorAction] as const;
